// `jd_links` is an enumeration (BE-Light serves ``, never null), and a field can legitimately carry
// SEVERAL links — a combined field carries one from each half, and that is the accurate state
// rather than a mess to be tidied.
import type { DashboardField } from "../types/contract";

export type JdSendGate =
  | { kind: "ready"; link: { jd_org_id: string; jd_field_id: string } }
  | { kind: "unlinked" }
  | { kind: "ambiguous"; links: { jd_org_id: string; jd_field_id: string }[] };

/*
 *
 * The array is the truth the scalar is only ever `jd_links[0]` (BE-Light shares one ORDER BY so the
 * two cannot drift) — but an older service that serves the scalar alone must still gate correctly
 * rather than reading as unlinked and sending the user off to re-link a field that is already
 * linked.
 */
export function jdSendGate(field: DashboardField): JdSendGate {
  const links = field.jd_links?.length
    ? field.jd_links
    : field.jd_link
      ? [field.jd_link]
      : [];
  if (links.length === 0) return { kind: "unlinked" };
  if (links.length === 1) return { kind: "ready", link: links[0]! };
  return { kind: "ambiguous", links };
}

/*
 * What the user reads when the gate is not `ready`. Says the state, then the way out of it.
 * `titleMany` is the multi-field variant — a singular title over a list of three fields reads as a
 * description of one of them.
 */
export const GATE_COPY = {
  unlinked: {
    title: "This field isn’t connected to John Deere yet",
    titleMany: "These fields aren’t connected to John Deere yet",
    body: "A spread map has to go to a specific John Deere field, so connect this one first. Nothing else changes — you’ll come straight back here.",
    action: "Connect a John Deere field",
  },
  ambiguous: {
    // Names the CAUSE, because a user who never combined anything would otherwise read this as a
    // bug.
    title: "This field is connected to more than one John Deere field",
    titleMany: "These fields are connected to more than one John Deere field",
    body: "That happens when fields are combined — the new field carries a connection from each one. Pick which John Deere field this spread should go to.",
    action: "Choose",
  },
} as const;

// The link a send will use: the single ready link, or the user's explicit pick
// when the field is connected to more than one John Deere field.
export function chosenJdLink(
  gate: JdSendGate | undefined,
  fieldId: string,
  chosen: Readonly<Record<string, string>>,
): { jd_org_id: string; jd_field_id: string } | null {
  if (gate?.kind === "ready") return gate.link;
  if (gate?.kind === "ambiguous") {
    const pick = chosen[fieldId];
    return (
      gate.links.find((l) => `${l.jd_org_id}:${l.jd_field_id}` === pick) ?? null
    );
  }
  return null;
}

/* * Every selected field, bucketed by gate state — what the send screen actually renders from. */
export function gateAll(
  fields: DashboardField[],
): { field: DashboardField; gate: JdSendGate }[] {
  return fields.map((field) => ({ field, gate: jdSendGate(field) }));
}
